// LicenseBadge — compact tier indicator for the top nav (T191).
//
// Renders nothing while loading or on fetch error (avoids layout shift on
// every page load). Click routes to /admin/license.

import { Link } from 'react-router-dom';
import { useLicenseState, LicenseTier } from '../lib/useLicenseState';

interface BadgeConfig {
  label: string;
  className: string;
}

// Returns null for a tier this component has no badge for — an unknown or
// future value from the API. Before 2026-10-02 the switch fell off the end
// and the render below dereferenced `undefined.className`, which took the
// whole TopNav (and with it every route) down with a TypeError. A nav badge
// must never be able to blank the app.
function badgeFor(tier: LicenseTier, daysRemaining: number): BadgeConfig | null {
  switch (tier) {
    case 'grace':
      return {
        label: daysRemaining >= 0 ? `No License · ${daysRemaining}d left` : 'No License',
        className: 'bg-danger/15 text-danger border-danger/30',
      };
    case 'expired':
      return {
        label: 'Read-only · License required',
        className: 'bg-danger/20 text-danger border-danger/40',
      };
    case 'trial':
      return {
        label: daysRemaining >= 0 ? `Trial · ${daysRemaining}d left` : 'Trial',
        className: 'bg-warning/15 text-warning border-warning/30',
      };
    case 'enterprise':
      return {
        label: 'Enterprise',
        className: 'bg-success/15 text-success border-success/30',
      };
    default:
      return null;
  }
}

export function LicenseBadge() {
  const { state, loading } = useLicenseState();

  if (loading || !state) return null;
  // Enterprise with no expiry approaching — show nothing to avoid nav clutter.
  if (state.tier === 'enterprise') return null;

  const cfg = badgeFor(state.tier, state.days_remaining);
  if (!cfg) return null;

  return (
    <Link
      to="/admin/license"
      className={[
        'shrink-0 inline-flex items-center px-2 py-0.5 rounded border',
        'text-xs font-semibold tracking-wide transition-opacity hover:opacity-80',
        cfg.className,
      ].join(' ')}
      title="License status — click to manage"
    >
      {cfg.label}
    </Link>
  );
}
